<script setup lang="ts">
import { useRoute, RouterView, useRouter, onBeforeRouteLeave } from "vue-router";
import { computed, onMounted, onUnmounted, watch } from "vue";

import { useApplicationStore, ApplyPageStages } from "../stores/application";
import ApplicationBanner from "../components/ApplicationBanner.vue";
import { useQuoteStore } from "../stores/quotes";
import { ensureDefined } from "../utils/types";
import { useNavigation } from "@/views/Apply/composables/useNavigation";

const quoteStore = useQuoteStore();
const application = useApplicationStore();

const route = useRoute();
const router = useRouter();
const { nextStep, goBack, isNotDownloadOrPostPackPages } = useNavigation(router);

const applicationStage = computed(() => application.applicationStage);

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = "";
};

onBeforeRouteLeave(() => {
    // eslint-disable-next-line no-alert
    return window.confirm("Are you sure you want to leave this page?");
});

onMounted(async () => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    const quoteNumber = route.query.quoteNumber?.toString();
    const premiumId = route.query.premiumId?.toString();
    if (!application.quoteNumber ||
        application.quoteNumber !== quoteNumber ||
        application.premiumId !== Number(premiumId)
    ) {
        await getQuotePremium(quoteNumber, premiumId);
    }
});

onUnmounted(() => {
    window.removeEventListener("beforeunload", handleBeforeUnload);
});

watch(() => route.meta.formStage, () => {
    if (route.meta?.formStage !== undefined) {
        application.applicationStage = +route.meta.formStage;
    }
}, { immediate: true });

const getQuotePremium = async (quoteNumber: string | undefined, premiumId: string | undefined) => {
    const premium = await quoteStore.getPremium(quoteNumber, premiumId);
    application.setApplicationDetails(premium, quoteNumber ?? "", ensureDefined(quoteStore.quote));
};

</script>
<template>
    <div class="mt-2">
        <div
            class="d-flex"
        >
            <button
                data-testid="goBack"
                type="button"
                class="mb-3 btn btn-secondary font-weight-bold"
                :class="[application.isPolling ? 'disabled' : '']"
                :disabled="application.isPolling"
                @click="application.isPolling ? '' : goBack()"
            >
                <span>{{ applicationStage === ApplyPageStages.Information || !isNotDownloadOrPostPackPages ? "Back to results" : "Back" }}</span>
            </button>
        </div>
        <div
            v-if="applicationStage > 1"
            class="mb-3 disable-selection questiontitle"
        >
            <div class="pt-header-small">
                Your application
                <span
                    v-if="isNotDownloadOrPostPackPages"
                    class="subheading"
                >
                    This application will take approximately 11 minutes
                </span>
            </div>
        </div>
        <ApplicationBanner v-if="isNotDownloadOrPostPackPages" />
        <RouterView />
        <button
            v-if="applicationStage !== ApplyPageStages.NearlyThere
                && applicationStage !== ApplyPageStages.CompleteAndSend
                && isNotDownloadOrPostPackPages"
            data-testid="continue"
            type="button"
            class="mb-3 btn btn-forward font-weight-bold mt-3"
            @click="nextStep()"
        >
            <span>Continue to apply</span>
        </button>
    </div>
</template>

<style scoped>
</style>
